import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './UserMenu.css';

const UserMenu = ({ onNavigate }) => {
  const { user, logout, openAuthModal } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const hoverTimeoutRef = useRef(null);


  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cleanup timeout on unmount
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    setIsMenuOpen(false);
  };

  const handleLoginClick = () => {
    if (!user) {
      openAuthModal('login');
    } else {
      // For logged-in users, navigate to account page
      setIsMenuOpen(false); // Close the menu when navigating
      onNavigate('account');
    }
  };

  const handleMenuHover = () => {
    if (user) {
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setIsMenuOpen(true);
    }
  };

  const handleMenuLeave = () => {
    // Add a longer delay before closing the menu to allow clicking
    hoverTimeoutRef.current = setTimeout(() => {
      setIsMenuOpen(false);
    }, 1000); // 1000ms delay - longer to allow clicking
  };

  const handleMenuItemClick = (section) => {
    // Prevent menu from closing immediately
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    // Store the section to be activated in sessionStorage for the AccountPage to read
    sessionStorage.setItem('activeAccountSection', section);
    
    // Navigate to account page with specific section
    onNavigate('account');
    
    // Close menu after navigation
    setIsMenuOpen(false);
  };

  if (!user) {
    return (
      <div className="user-menu-container" ref={menuRef}>
        <button 
          className="login-icon-btn"
          onClick={handleLoginClick}
          title="Login"
        >
          <i className="fas fa-user"></i>
        </button>
      </div>
    );
  }

  return (
    <div 
      className="user-menu-container" 
      ref={menuRef}
    >
      <button 
        className="user-avatar-btn"
        onClick={handleLoginClick}
        onMouseEnter={handleMenuHover}
        onMouseLeave={handleMenuLeave}
        title={user.displayName || user.email}
      >
        <div className="user-avatar">
          {user.photoURL ? (
            <img src={user.photoURL} alt={user.displayName} />
          ) : (
            <i className="fas fa-user"></i>
          )}
        </div>
      </button>

      <div 
        className={`user-menu ${isMenuOpen ? 'open' : ''}`}
        onMouseEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
          }
        }}
        onMouseLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleMenuLeave();
        }}
      >
        <div className="menu-header">
          <div className="user-info">
            <div className="user-name">{user.displayName || 'User'}</div>
            <div className="user-email">{user.email}</div>
          </div>
        </div>
        
        <div className="menu-divider"></div>
        
        <div className="menu-items">
          <button 
            className="menu-item" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleMenuItemClick('profile');
            }}
          >
            <i className="fas fa-user"></i>
            <span>Profile</span>
          </button>
          
          <button 
            className="menu-item" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleMenuItemClick('tickets');
            }}
          >
            <i className="fas fa-ticket-alt"></i>
            <span>My Tickets</span>
          </button>
          
          <button 
            className="menu-item" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleMenuItemClick('messages');
            }}
          >
            <i className="fas fa-comments"></i>
            <span>Messages</span>
          </button>
          
          <button 
            className="menu-item" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleMenuItemClick('settings');
            }}
          >
            <i className="fas fa-cog"></i>
            <span>Settings</span>
          </button>
        </div>
        
        <div className="menu-divider"></div>
        
        <div className="menu-footer">
          <button 
            className="menu-item logout" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleLogout();
            }}
          >
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserMenu;
